/**
 * Share watermarked design previews with clients (designer / trade workflow).
 */
(function sharePreviewModule() {
  const formatPrice = (gen, amountMajor) => {
    if (!Number.isFinite(amountMajor) || amountMajor <= 0) return '—';
    try {
      return new Intl.NumberFormat(gen.locale, {
        style: 'currency',
        currency: gen.currencyIso,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amountMajor);
    } catch {
      return `${gen.currencyFallback || ''}${Math.round(amountMajor).toLocaleString(gen.locale)}`;
    }
  };

  class DesignShareController {
    /** @param {any} generator */
    constructor(generator) {
      this.gen = generator;
      this.root = generator.root;
      this.endpoint =
        this.root.dataset.sharePreviewEndpoint || '/apps/wallprint-ai/share-preview';
      this.dialog = this.root.querySelector('[data-ai-share-dialog]');
      this.openBtn = this.root.querySelector('[data-ai-share-open]');
      this.closeBtn = this.root.querySelector('[data-ai-share-close]');
      this.form = this.root.querySelector('[data-ai-share-form]');
      this.descInput = this.root.querySelector('[data-ai-share-description]');
      this.notesInput = this.root.querySelector('[data-ai-share-notes]');
      this.includePriceInput = this.root.querySelector('[data-ai-share-include-price]');
      this.createBtn = this.root.querySelector('[data-ai-share-create]');
      this.copyBtn = this.root.querySelector('[data-ai-share-copy]');
      this.statusEl = this.root.querySelector('[data-ai-share-status]');
      this.urlInput = this.root.querySelector('[data-ai-share-url]');
      this.spinner = this.root.querySelector('[data-ai-share-spinner]');
      this.introEl = this.root.querySelector('[data-ai-share-intro]');
      this._shareUrl = '';

      this.openBtn?.addEventListener('click', (event) => {
        if (this.#handleLockedOpen(event)) return;
        this.openDialog();
      });
      this.closeBtn?.addEventListener('click', () => this.dialog?.close());
      this.dialog?.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.createShare();
      });
      this.copyBtn?.addEventListener('click', () => this.copyLink());
      this.syncButton(this.gen.calculate());
    }

    syncButton(state = this.gen.calculate()) {
      if (!this.openBtn) return;
      const hasDesign = Boolean(this.gen.currentAsset);
      const hasVisual = Boolean(this.#previewSrc());
      const busy =
        this.gen.isGenerating || this.gen.isUploading || this.gen.isTiling || this.gen.isMockup;
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      const ready = hasDesign && hasVisual && hasDims;
      this.openBtn.hidden = false;
      this.openBtn.disabled = busy;
      this.openBtn.setAttribute('aria-disabled', String(!ready || busy));
      this.openBtn.classList.toggle('is-preview-teaser', !ready);
      this.openBtn.classList.toggle('is-action-locked', !ready && !busy);
      this.openBtn.title = !ready
        ? 'Add wall measurements and a design first'
        : this.#mockupPreviewSrc()
          ? 'Share your project — design and room mockup — with your client'
          : 'Share a watermarked project link with your client';
    }

    #handleLockedOpen(event) {
      if (!this.openBtn) return false;
      const busy =
        this.gen.isGenerating || this.gen.isUploading || this.gen.isTiling || this.gen.isMockup;
      if (busy) {
        event.preventDefault();
        return true;
      }
      if (this.openBtn.getAttribute('aria-disabled') !== 'true') return false;
      event.preventDefault();
      const state = this.gen.calculate();
      const hasDesign = Boolean(this.gen.currentAsset);
      const hasVisual = Boolean(this.#previewSrc());
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      if (!hasDesign || !hasVisual) {
        this.gen.reportError?.('Add a design first — generate or upload in the panel on the left.');
        this.gen.scrollToStudioSection?.('design');
        return true;
      }
      if (!hasDims) {
        this.gen.reportError?.('Enter your wall width and height first.');
        this.gen.scrollToStudioSection?.('wall');
        return true;
      }
      return true;
    }

    #resolveSharePreviewUrl(url) {
      if (!url || typeof url !== 'string') return url;
      try {
        const asset = new URL(url, window.location.href);
        const endpoint = this.gen.generateEndpoint || this.gen.uploadEndpoint || '';
        if (
          (asset.hostname === 'localhost' || asset.hostname === '127.0.0.1') &&
          /^https?:\/\//i.test(endpoint)
        ) {
          const api = new URL(endpoint);
          asset.protocol = api.protocol;
          asset.host = api.host;
          return asset.toString();
        }
        return url;
      } catch {
        return url;
      }
    }

    async #previewSrcForShare() {
      const g = this.gen;
      const asset = g.currentAsset;
      if (asset?.previewUrl && g.isServerBackedAsset?.(asset)) {
        return this.#resolveSharePreviewUrl(asset.previewUrl);
      }
      const visual = this.#previewSrc();
      if (visual.startsWith('blob:')) {
        try {
          const res = await fetch(visual);
          const blob = await res.blob();
          return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch {
          return visual;
        }
      }
      return visual;
    }

    #previewSrc() {
      const g = this.gen;
      if (g.cropViewport && !g.cropViewport.hidden && g.cropImage?.src) return g.cropImage.src;
      if (g.image && !g.image.hidden && g.image.src) return g.image.src;
      return g.currentAsset?.previewUrl || '';
    }

    #mockupPreviewSrc() {
      return this.gen.lastMockupUrl || '';
    }

    openDialog() {
      if (!this.dialog || !this.gen.currentAsset) return;
      const state = this.gen.calculate();
      const asset = this.gen.currentAsset;
      const lu = state.lengthUnit;
      const wallSize = `${state.widthDisp} × ${state.heightDisp} ${lu}`;
      const promptBit =
        asset.source === 'generated' && asset.prompt
          ? ` — ${asset.prompt.slice(0, 64)}${asset.prompt.length > 64 ? '…' : ''}`
          : '';

      if (this.descInput) {
        this.descInput.value = `${this.gen.productTitle || 'Wallpaper'} · ${wallSize}${promptBit}`;
      }
      if (this.notesInput) this.notesInput.value = '';
      if (this.includePriceInput) this.includePriceInput.checked = false;
      if (this.introEl) {
        this.introEl.textContent = this.#mockupPreviewSrc()
          ? 'Creates a watermarked link with your room mockup, design preview, wall size, and panel count — preview only, not a print file.'
          : 'Creates a watermarked link your client can open — design preview with wall size and panels. Add a room mockup first to include it in the share.';
      }
      this._shareUrl = '';
      if (this.urlInput) {
        this.urlInput.value = '';
        this.urlInput.hidden = true;
      }
      if (this.copyBtn) this.copyBtn.hidden = true;
      this.#status('');

      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    async createShare() {
      if (!this.gen.currentAsset) return;
      const state = this.gen.calculate();
      const previewUrl = await this.#previewSrcForShare();
      if (!previewUrl) {
        this.#status('No preview image available yet.', true);
        return;
      }

      const lu = state.lengthUnit;
      const wallSize = `${state.widthDisp} × ${state.heightDisp} ${lu}`;
      const printSize = `${state.printWidthDisp || state.widthDisp} × ${state.printHeightDisp || state.heightDisp} ${lu}`;
      const asset = this.gen.currentAsset;
      const mockupPreviewUrl = this.#mockupPreviewSrc();
      const body = {
        previewUrl,
        mockupPreviewUrl,
        room_id: this.gen.lastMockupRoomId || '',
        description: this.descInput?.value?.trim() || '',
        personalNotes: this.notesInput?.value?.trim() || '',
        includePrice: Boolean(this.includePriceInput?.checked),
        priceLabel: formatPrice(this.gen, state.price),
        wallSize,
        printSize,
        panels: state.panels > 1 ? `${state.panels} panels` : 'Single panel',
        productTitle: this.gen.productTitle || 'WallPrint design',
        sourceLabel: asset.source === 'uploaded' ? 'Uploaded design' : 'AI generated design',
        watermark_logo_url: this.gen.watermarkLogoUrl || '',
      };

      this.createBtn.disabled = true;
      this.spinner?.classList.add('is-active');

      try {
        const fetchFn =
          typeof this.gen.customerApiFetch === 'function'
            ? this.gen.customerApiFetch.bind(this.gen)
            : fetch;
        const res = await fetchFn(this.endpoint, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || data.error || 'Could not create share link.');
        }
        this._shareUrl = data.share_url || '';
        if (!this._shareUrl) throw new Error('Share link missing from server response.');
        if (this.urlInput) {
          this.urlInput.value = this._shareUrl;
          this.urlInput.hidden = false;
        }
        if (this.copyBtn) this.copyBtn.hidden = false;
        this.#status(
          mockupPreviewUrl
            ? 'Share link ready — includes your room mockup and design preview.'
            : 'Share link ready — copy and send to your client.'
        );
        if (navigator.share) {
          try {
            await navigator.share({
              title: `${body.productTitle} preview`,
              text: body.personalNotes || body.description,
              url: this._shareUrl,
            });
          } catch {
            /* cancelled */
          }
        }
      } catch (err) {
        this.#status(err.message || 'Share failed.', true);
      } finally {
        this.createBtn.disabled = false;
        this.spinner?.classList.remove('is-active');
      }
    }

    async copyLink() {
      if (!this._shareUrl) return;
      try {
        await navigator.clipboard.writeText(this._shareUrl);
        this.#status('Link copied to clipboard.');
      } catch {
        if (this.urlInput) {
          this.urlInput.hidden = false;
          this.urlInput.select();
        }
        this.#status('Select the link above and copy manually.');
      }
    }

    #status(message, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message;
      this.statusEl.hidden = !message;
      this.statusEl.classList.toggle('is-error', Boolean(isError));
    }
  }

  const attach = () => {
    document.querySelectorAll('[data-ai-generator]').forEach((root) => {
      const gen = root.wallprintAiGenerator;
      if (!gen || root.wallprintAiDesignShare) return;
      root.wallprintAiDesignShare = new DesignShareController(gen);
    });
  };

  document.addEventListener('wallprint:ai-generator-ready', attach);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
  document.addEventListener('shopify:section:load', attach);
})();
